import { useEffect, useMemo, useRef, useState } from 'react';

import { graphql, useFragment } from 'react-relay';

import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import {
	type HydratedUserResult,
	useHydratedUsers,
} from '@atlassian/jira-platform-fields/useHydratedUsers.tsx';
import { useStableArray } from '@atlassian/jira-platform-fields/useStableArray.tsx';

import { PARENT_FIELD_ID, USER_PICKER_FIELD_TYPES } from '../../common/constants';
import type { DraftWorkItemWithHierarchy } from '../../common/types';
import { buildIssueTypeToFieldsConfiguration } from '../../utils/buildIssueTypeToFieldsConfiguration';
import { buildUniqueFieldsConfiguration } from '../../utils/buildUniqueFieldsConfiguration';
import { toDraftWorkItems } from '../../utils/toDraftWorkItems';
import { useUniqueIssueTypeIds } from '../../utils/useUniqueIssueTypeIds';
import { useUniqueSuggestedField } from '../../utils/useUniqueSuggestedField';
import { useDraftWorkItemsActions } from '../draft-work-items-store';
import { useGetDraftWorkItemsWithHierarchy } from '../draft-work-items-store/hooks';
import { useDraftWorkItems as useDraftWorkItemsNew } from '../useDraftWorkItemsNew';

import type {
	useAiCreateProjectContextQuery$data,
	useAiCreateProjectContextQuery$key,
} from './__generated__/useAiCreateProjectContextQuery.graphql';
import type {
	FieldConfiguration,
	IssueTypeEdgesLike,
	IssueTypeToFieldsConfiguration,
	JiraCreateItemsAction,
} from './types';

const ISSUEKEY_REGEX = /^[A-Za-z0-9]+-\d+$/;
function isValidJiraIssueKey(value: string): boolean {
	return ISSUEKEY_REGEX.test(value);
}

const HYDRATION_FALLBACK_TIMEOUT_MS = 1000;
function useHasLoadedOrTimedOut({
	isHydrationDone,
	hydratedUsersResults,
	messageId,
	sendAifcAnalyticsEvent,
}: {
	isHydrationDone: boolean;
	hydratedUsersResults: HydratedUserResult[];
	messageId: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}): boolean {
	const [hasTimedOut, setHasTimedOut] = useState(false);
	const hasReportedTimeoutRef = useRef(false);

	useEffect(() => {
		if (isHydrationDone) {
			return;
		}
		const timeoutId = setTimeout(() => {
			setHasTimedOut(true);
		}, HYDRATION_FALLBACK_TIMEOUT_MS);
		return () => clearTimeout(timeoutId);
	}, [isHydrationDone]);

	useEffect(() => {
		if (!hasTimedOut || isHydrationDone || hasReportedTimeoutRef.current) {
			return;
		}
		hasReportedTimeoutRef.current = true;

		sendAifcAnalyticsEvent?.({
			action: 'failed',
			actionSubject: 'userPickerHydration',
			actionSubjectId: 'aiCreateProjectContext',
			attributes: {
				messageId,
				totalCount: hydratedUsersResults.length,
				readyCount: hydratedUsersResults.filter((r) => r.status === 'READY').length,
				erroredCount: hydratedUsersResults.filter((r) => r.status === 'ERRORED').length,
				stillLoadingCount: hydratedUsersResults.filter(
					(r) => r.status === 'LOADING' || r.status === 'INIT',
				).length,
			},
		});
	}, [hasTimedOut, isHydrationDone, hydratedUsersResults, messageId, sendAifcAnalyticsEvent]);

	return isHydrationDone || hasTimedOut;
}

export type Props = {
	project: useAiCreateProjectContextQuery$key;
	cloudId?: string;
	actions: JiraCreateItemsAction[];
	messageId: string;
	conversationId?: string;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
	isActiveMessage?: boolean;
};

const useAllHydratedUsersFromActions = (
	actions: JiraCreateItemsAction[],
	issueTypeMap: IssueTypeToFieldsConfiguration,
): {
	hydratedUsersMap: Record<string, HydratedUserResult>;
	hydratedUsersResults: HydratedUserResult[];
} => {
	const allAccountIds = useMemo(() => {
		const accountIds: string[] = [];
		actions.forEach((action) => {
			return Object.entries(action.data.fields).forEach(([fieldId, value]) => {
				const fieldType =
					issueTypeMap?.[action?.data?.issueTypeId]?.fieldsConfigurationNode?.[fieldId];
				if (USER_PICKER_FIELD_TYPES.includes(fieldType?.__typename ?? '')) {
					accountIds.push(...(Array.isArray(value) ? value : [value]));
				}
			});
		});
		return accountIds;
	}, [actions, issueTypeMap]);

	const dedupedAccountIds = useStableArray(allAccountIds, { sort: false, deduplicate: true });

	const hydratedUsersResults = useHydratedUsers({
		accountIds: dedupedAccountIds,
		shouldHydrate: true,
	});

	const hydratedUsersMap = useMemo(() => {
		return hydratedUsersResults.reduce(
			(acc, result) => {
				if (result.id) {
					acc[result.id] = result;
				}
				return acc;
			},
			{} as Record<string, HydratedUserResult>,
		);
	}, [hydratedUsersResults]);

	return {
		hydratedUsersMap,
		hydratedUsersResults,
	};
};

const withValidateIssueTypes = (
	actions: JiraCreateItemsAction[],
	issueTypes: IssueTypeEdgesLike,
) => {
	const validIssueTypeIds = new Set(
		issueTypes.map((edge) => edge?.node?.issueTypeId).filter(Boolean),
	);
	const defaultIssueTypeId = issueTypes.find((edge) => edge?.node?.hierarchy?.level === 0)?.node
		?.issueTypeId;

	return actions.map((action) => {
		if (validIssueTypeIds.has(action.data.issueTypeId)) {
			return action;
		}

		if (!defaultIssueTypeId) {
			return action;
		}

		return {
			...action,
			data: {
				...action.data,
				issueTypeId: defaultIssueTypeId,
			},
		};
	});
};

const useDraftWorkItemsState = ({
	actions,
	data,
	messageId,
	conversationId,
	cloudId,
	isActiveMessage,
	sendAifcAnalyticsEvent,
}: {
	actions: JiraCreateItemsAction[];
	data: useAiCreateProjectContextQuery$data;
	messageId: string;
	conversationId?: string;
	cloudId?: string;
	isActiveMessage?: boolean;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}) => {
	const issueTypeMap = useMemo(() => {
		return buildIssueTypeToFieldsConfiguration(data.jira_projectByIdOrKey?.issueTypes?.edges ?? []);
	}, [data.jira_projectByIdOrKey]);

	const { hydratedUsersMap, hydratedUsersResults } = useAllHydratedUsersFromActions(
		actions,
		issueTypeMap,
	);

	const { invocationIdToWorkItemMap, topLevelInvocationIds } = useMemo(() => {
		return toDraftWorkItems({
			actions,
			issueTypeMap,
			hydratedUsersMap,
		});
	}, [actions, issueTypeMap, hydratedUsersMap]);

	const isHydrationDone = useMemo(() => {
		return hydratedUsersResults.every((result) => result.status === 'READY');
	}, [hydratedUsersResults]);

	const draftWorkItemsActions = useDraftWorkItemsNew({
		messageId,
		cloudId,
		conversationId,
		isActiveMessage,
		sendAifcAnalyticsEvent,
	});
	const draftWorkItems = useGetDraftWorkItemsWithHierarchy(messageId);
	const { initDraftWorkItems } = useDraftWorkItemsActions();

	// Wait for user-picker hydration before initialising the store.
	// To avoid blocking the card forever if hydration is slow or fails, fall back to writing
	// the (possibly unhydrated) map.
	const hasLoadedOrTimedOut = useHasLoadedOrTimedOut({
		isHydrationDone,
		hydratedUsersResults,
		messageId,
		sendAifcAnalyticsEvent,
	});

	useEffect(() => {
		if (!hasLoadedOrTimedOut) {
			return;
		}
		initDraftWorkItems(messageId, invocationIdToWorkItemMap, topLevelInvocationIds);
	}, [
		messageId,
		invocationIdToWorkItemMap,
		topLevelInvocationIds,
		initDraftWorkItems,
		hasLoadedOrTimedOut,
	]);

	return {
		draftWorkItems,
		// Whether user pickers have been hydrated.
		isHydrationDone,
		// Whether the store has been initialised. Until this is
		isStoreReady: hasLoadedOrTimedOut,
		...draftWorkItemsActions,
	};
};

export function useAiCreateProjectContext({
	project,
	actions,
	messageId,
	isActiveMessage,
	conversationId,
	cloudId,
	sendAifcAnalyticsEvent,
}: Props): {
	onWorkItemsCreated: ({
		invocationIdToCreatedIssueKey,
	}: {
		invocationIdToCreatedIssueKey: Record<string, string>;
	}) => void;
	onDismissAllDraftWorkItems: () => void;
	onFieldChange: (invocationId: string, fieldId: string, newValue: unknown) => void;
	removeWorkItem: (invocationId: string) => boolean;
	uniqueFieldsConfiguration: Record<string, FieldConfiguration>;
	isHydrationDone: boolean;
	isStoreReady: boolean;
	projectInfo: {
		projectKey: string | undefined;
		projectId: string | null | undefined;
	};
	draftWorkItems: DraftWorkItemWithHierarchy[];
} {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks, @atlassian/relay/graphql-naming, @atlassian/relay/unused-fields */
	const data = useFragment<useAiCreateProjectContextQuery$key>(
		graphql`
			fragment useAiCreateProjectContextQuery on Query
			@argumentDefinitions(cloudId: { type: "ID!" }, projectIdOrKey: { type: "String!" }) {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
					projectId
					key
					issueTypes {
						edges {
							node {
								issueTypeId
								name
								hierarchy {
									level
								}
								avatar {
									small
								}
								fieldsConfiguration @optIn(to: "JiraIssueTypeFieldsConfiguration") {
									edges {
										node {
											__typename
											type
											fieldId
											name
											... on JiraIssueFieldConfiguration {
												fieldConfig {
													isEditable
													isRequired
												}
											}
											... on JiraComponentsField {
												components {
													edges {
														node {
															componentId
															name
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		project,
		/* eslint-enable @atlassian/relay/no-wrapping-hooks, @atlassian/relay/graphql-naming, @atlassian/relay/unused-fields */
	);

	const validatedActions = useMemo(
		() => withValidateIssueTypes(actions, data.jira_projectByIdOrKey?.issueTypes?.edges ?? []),
		[actions, data.jira_projectByIdOrKey?.issueTypes?.edges],
	);

	const { draftWorkItems, isHydrationDone, ...draftWorkItemsActions } = useDraftWorkItemsState({
		actions: validatedActions,
		data,
		messageId,
		isActiveMessage,
		conversationId,
		cloudId,
		sendAifcAnalyticsEvent,
	});

	const uniqueFieldIds = useUniqueSuggestedField(draftWorkItems);

	const uniqueIssueTypeIds = useUniqueIssueTypeIds(draftWorkItems);

	const draftItemHasExistingParent = useMemo(
		() =>
			draftWorkItems.some((item) => {
				const parent = item.fields[PARENT_FIELD_ID];
				return typeof parent === 'string' && isValidJiraIssueKey(parent);
			}),
		[draftWorkItems],
	);

	const uniqueFieldsConfiguration = useMemo(
		() =>
			buildUniqueFieldsConfiguration(
				data.jira_projectByIdOrKey,
				uniqueIssueTypeIds,
				uniqueFieldIds,
				draftItemHasExistingParent,
			),
		[data.jira_projectByIdOrKey, uniqueIssueTypeIds, uniqueFieldIds, draftItemHasExistingParent],
	);

	const projectInfo = useMemo(
		() => ({
			projectKey: data.jira_projectByIdOrKey?.key,
			projectId: data.jira_projectByIdOrKey?.projectId,
		}),
		[data.jira_projectByIdOrKey?.key, data.jira_projectByIdOrKey?.projectId],
	);

	return {
		uniqueFieldsConfiguration,
		isHydrationDone,
		projectInfo,
		draftWorkItems,
		...draftWorkItemsActions,
	};
}
